// Sign Up Page Component
// Implements user registration with email/password and display name

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSignUp } from '../hooks/useAuth';
import { signUpSchema, type SignUpData } from '../lib/schemas';
import { getErrorMessage } from '../lib/firebase-errors';
import {
  AccountCreatedSignInFailedError,
  SignUpRequestError,
} from '../lib/auth/signUpError';
import {
  FormField,
  FormButton,
  FormError,
  PasswordField,
} from '../components/form';
import { isPRPreviewEnvironment } from '../lib/utils/environment';

// Build the form validation schema, conditionally requiring access_code when
// the environment flag is set.  Evaluated inside a function so that tests
// (which mutate import.meta.env between runs) always get the current value.
function buildSignUpSchema() {
  if (import.meta.env.VITE_SIGNUP_ACCESS_CODE_REQUIRED === 'true') {
    return signUpSchema.refine(
      data => !!data.access_code && data.access_code.length > 0,
      {
        message: 'Access code is required',
        path: ['access_code'],
      }
    );
  }
  return signUpSchema;
}

// What the banner above the form shows. `refused` is the API's generic 403 and
// carries no text on purpose: its wording is fixed here, not taken from the
// response.
type GlobalError = { kind: 'refused' } | { kind: 'message'; text: string };

export function SignUpPage() {
  // Carried onto the links out of this page so a relying-party sign-in in
  // progress survives them (#1831). `PublicRoute` reads it back.
  const location = useLocation();
  const navigate = useNavigate();
  const loginHint = (location.state as { loginHint?: string } | null)
    ?.loginHint;
  const signUpMutation = useSignUp();
  const [globalError, setGlobalError] = useState<GlobalError | null>(null);
  const resolvedSignUpSchema = buildSignUpSchema();
  // Per environment, never per address or per refusal reason: it decides both
  // whether the field renders and whether the refusal copy mentions it.
  const accessCodeShown =
    import.meta.env.VITE_SIGNUP_ACCESS_CODE_REQUIRED === 'true';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SignUpData>({
    resolver: zodResolver(resolvedSignUpSchema),
    // Prefilled from the relying party's `login_hint` when there is one. At a
    // partner's cutover this is what puts the address their subscription
    // records are keyed on in front of the right person — type a different one
    // and the subscription silently stops matching. Editable, deliberately: it
    // is a hint, and the person may genuinely want a different address.
    defaultValues: { email: loginHint },
  });

  const onSubmit = async (data: SignUpData) => {
    try {
      setGlobalError(null);
      await signUpMutation.mutateAsync(data);
      // Navigation will be handled by PublicRoute based on user status
      // User will be redirected to email verification
    } catch (error) {
      // The account was created; only the sign-in after it failed (#2123).
      // Staying here would be a dead end: submitting again is refused,
      // because the address is now in use. So they go to sign in, with the
      // address they just registered.
      //
      // `location.state` is spread so a pending relying-party sign-in
      // (`returnTo`) and the partner's `loginHint` survive, as they do on the
      // links below. The address travels under its own key, not as
      // `loginHint`: that would overwrite the partner's hint, and the sign-in
      // page would say the application suggested an address this person typed.
      //
      // `replace`, so Back does not return to a form that can only refuse.
      if (error instanceof AccountCreatedSignInFailedError) {
        navigate('/', {
          replace: true,
          state: {
            ...(location.state as Record<string, unknown> | null),
            accountCreatedEmail: data.email,
          },
        });
        return;
      }
      // Decided from the status alone, never from the response body (#2077).
      // The API answers every refusal — an address already in use, one
      // submitted moments ago, a wrong or missing access code — with the same
      // 403, deliberately: telling them apart is an account-existence oracle
      // (#1862). Copy chosen from the message would reopen that the day the
      // messages diverged.
      if (error instanceof SignUpRequestError && error.status === 403) {
        setGlobalError({ kind: 'refused' });
        return;
      }
      setGlobalError({ kind: 'message', text: getErrorMessage(error) });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-6 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-gray-900 leading-tight">
            Create Your Heapster ID
          </h1>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10 border border-gray-200">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {globalError?.kind === 'message' && (
              <FormError>{globalError.text}</FormError>
            )}
            {globalError?.kind === 'refused' && (
              <FormError>
                We couldn&apos;t create an account with these details. If you
                already have an account,{' '}
                <Link
                  to="/forgot-password"
                  // A relying-party sign-in that sent this person here is still
                  // waiting. The reset page puts it in the email's continue
                  // URL, but only if it is handed the state (#1831).
                  state={location.state}
                  className="underline hover:text-red-900 transition-colors duration-200"
                >
                  reset your password
                </Link>
                .
                {accessCodeShown &&
                  ' Otherwise, check your access code and try again.'}
              </FormError>
            )}

            <FormField
              {...register('display_name')}
              label="Display Name"
              type="text"
              autoComplete="username"
              placeholder="How should others see you?"
              error={errors.display_name?.message}
              helpText="This is how you'll appear to other users. You can change this later."
              isLoading={signUpMutation.isPending}
            />

            <FormField
              {...register('email')}
              label="Email Address"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="Enter your email"
              error={errors.email?.message}
              // Composed, never replaced: the verification promise below is
              // the one thing a person signing up needs to know, and an
              // earlier draft of #1947 that swapped it for the hint note
              // dropped it silently.
              //
              // Says something different from the sign-in page's note, and
              // deliberately so. There an account already exists and the
              // prefilled address may not be the one it uses, so that page
              // invites a change. Here no account exists yet, and this address
              // is the one the partner will look for when it tries to match
              // this person to what they already pay for. Steering them off it
              // would cause the very mismatch the hint exists to prevent.
              //
              // So this one gives the reason instead of the invitation. An
              // earlier draft ended "You can change it.", which was an
              // invitation with no stake attached — sitting directly under this
              // comment explaining why changing is the harmful move. The field
              // stays editable either way (the guide promises that, and the
              // test above pins it); what changes is whether we tell someone to
              // use it without telling them what it costs.
              helpText={[
                loginHint
                  ? 'This address was suggested by the application you came from, and is the one it will look for. Change it only if it is wrong.'
                  : null,
                isPRPreviewEnvironment()
                  ? 'Email verification is disabled in PR preview environments.'
                  : "We'll send you a verification email to confirm your account.",
              ]
                .filter(Boolean)
                .join(' ')}
              isLoading={signUpMutation.isPending}
            />

            <PasswordField
              {...register('password')}
              label="Password"
              autoComplete="new-password"
              placeholder="Create a strong password"
              error={errors.password?.message}
              helpText="Minimum 8 characters with a mix of letters and numbers."
              isLoading={signUpMutation.isPending}
            />

            {accessCodeShown && (
              <FormField
                {...register('access_code')}
                label="Access Code"
                type="text"
                placeholder="Enter your access code"
                error={errors.access_code?.message}
                helpText="Required for signup in this environment."
                isLoading={signUpMutation.isPending}
              />
            )}

            <FormButton
              type="submit"
              isLoading={signUpMutation.isPending}
              loadingText="Creating Account..."
            >
              Create Account
            </FormButton>
          </form>

          <div className="mt-6">
            <div className="text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link
                  to="/"
                  state={location.state}
                  className="font-medium text-sky-600 hover:text-sky-500 transition-colors duration-200"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <p className="text-xs text-center text-gray-500">
              By creating an account, you agree to our{' '}
              <Link
                to="/terms"
                className="font-medium text-sky-600 hover:text-sky-500 transition-colors duration-200"
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                to="/privacy"
                className="font-medium text-sky-600 hover:text-sky-500 transition-colors duration-200"
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
